import { useRef } from "react";
import { Button, CircularProgress, Skeleton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DownloadIcon } from "../../../../../assets/icons";
import { AppRoutes } from "../../../../../common/routesUtils";
import SimpleBarChart from "../../../../../components/charts/SimpleBarChart";
import PaperCard from "../../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../../components/widgets/PaperCard/PaperCardHeader";
import { PageType } from "../../../../../config/page.types";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import {
	EmissionsActivityResultsDTO,
	ExtendedMeasurableValueDTO,
	ResultsBasePayload,
} from "../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { useNavigationPreferences } from "../../../../../hooks/usePreferences";
import { useReadResource, useWriteResource } from "../../../../../hooks/useResource";
import { ThemeColors } from "../../../../../theme/themeValues";

type Props = {
	keyEmissions: string;
	chartMode: "tCO2e" | "% tCO2e";
	onChartModeChange: (v: "tCO2e" | "% tCO2e") => void;
};

const ResultsActivityEmissions = ({ keyEmissions, chartMode, onChartModeChange }: Props) => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	const { t } = useTranslation("feature.results");

	const { appliedFilters } = useFiltersRegistry();
	const { navigationPreferences } = useNavigationPreferences();

	const {
		data: response,
		reload,
		isLoading,
	} = useReadResource<ResultsBasePayload, EmissionsActivityResultsDTO>(
		{
			route: ApiRoutes.ResultsActivityEmissions,
			query: { perspective: navigationPreferences.transportsPerspective },
		},
		{
			readMethod: "post",
		},
		{ ...appliedFilters.payload }
	);

	const pdfResource = useWriteResource<ResultsBasePayload, File>(
		{ route: ApiRoutes.ResultsTranposrtPDFDownload },
		{ subjectKey: "download" }
	);

	const pdfOperationalUnits = useWriteResource<ResultsBasePayload, File>(
		{ route: ApiRoutes.OperationalUnitsPDFDownload },
		{ subjectKey: "download" }
	);

	const handlePdfDownload = (resource: typeof pdfResource, filename: string) => {
		resource.postPdf(
			{ ...appliedFilters.payload, perspective: navigationPreferences.transportsPerspective },
			filename
		);
	};

	// Helper function to transform API data into chart format
	const mapDataToChart = (data: EmissionsActivityResultsDTO["dataTtw"] | EmissionsActivityResultsDTO["dataWtw"]) =>
		Object.entries(data ?? {}).map(([key, value]: [string, ExtendedMeasurableValueDTO]) => {
			const getLink = (key: string) => {
				switch (key) {
					case "transport":
						return { route: AppRoutes.ResultsTransport, pageType: PageType.ResultsTransport };
					case "sites":
						return { route: AppRoutes.ResultsOperationalUnits, pageType: PageType.ResultsOperationalUnits };
					default:
						return undefined;
				}
			};

			return {
				label: t(`emissions/activity/${key}`, { ns: "feature.results" }),
				value: value.value,
				link: getLink(key),
				measure: value.measure,
				percentage: value.percentage,
				color: ThemeColors.LightSecondary,
			};
		});

	// Datasets to switch between them dynamically
	const chartData = {
		co2Ttw: response?.dataTtw ? mapDataToChart(response.dataTtw) : [],
		co2Wtw: response?.dataWtw ? mapDataToChart(response.dataWtw) : [],
	};

	const buttonActions = (
		<Stack pt={3} justifyContent={"center"} spacing={2} direction="row">
			<Stack direction="row" spacing={1}>
				<Button
					disabled={pdfResource?.isLoading || pdfOperationalUnits.isLoading}
					onClick={() => handlePdfDownload(pdfResource, "transport-report.pdf")}
					size="medium"
					color="primary"
					startIcon={pdfResource?.isLoading ? <CircularProgress size={18} /> : <DownloadIcon />}
				>
					{t("iso transport report")}
				</Button>
				<Button
					disabled={pdfResource?.isLoading || pdfOperationalUnits.isLoading}
					onClick={() => handlePdfDownload(pdfOperationalUnits, "operational-units-report.pdf")}
					size="medium"
					color="primary"
					startIcon={pdfOperationalUnits?.isLoading ? <CircularProgress size={18} /> : <DownloadIcon />}
				>
					{t("report fraunhofer")}
				</Button>
			</Stack>
		</Stack>
	);

	if (isLoading) return <Skeleton variant="rounded" height="100%" width="100%" />;

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader containerRef={containerRef} title={t("emissions/activity/title")} onReload={reload} />
			}
			content={
				<Stack direction="column" height={"100%"} width={"100%"} justifyContent={"flex-end"}>
					<SimpleBarChart
						data={chartData[keyEmissions as "co2Ttw" | "co2Wtw"]}
						chartName={`activities-emissions-${keyEmissions}`}
						onSeriesClicked={(index) => alert(index)}
						chartMode={chartMode}
						onChartModeChange={onChartModeChange}
					/>
					{buttonActions}
				</Stack>
			}
		/>
	);
};

export default ResultsActivityEmissions;
